import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  ChevronDown,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { SERVICES, COMPANY } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import {
  PillBadge,
  CTASection,
  SERVICE_ICONS,
  ScheduleCallButton,
} from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

const PHONE = "(604) 996 6862";
const PHONE_HREF = "tel:+16049966862";
const EMAIL = "info@abhifinancial.com";

const MAP_URL =
  "https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada";

const SUPER_VISA_SLUG = "super-visa-insurance";

const SUPER_VISA_META = {
  title:
    "Super Visa Insurance Surrey BC | Cheapest Rates and Monthly Plans | (604) 996 6862",
  description:
    "Super Visa insurance from Canada's leading insurers. $100,000 coverage, monthly payments, preexisting conditions covered, full refund if refused. Call (604) 996 6862.",
};

const PAGE_DISCLAIMER =
  "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

  const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

const SUPER_VISA_COPY = {
  heroLine: "Extending your stay in Canada",
  title: "Super Visa Insurance",
  intro:
    "If your parents or grandparents are coming to Canada for an extended stay, they will need Super Visa insurance to qualify for the visa. We compare policies across Canada's leading insurers to find you reliable coverage at the lowest available rate, with a free consultation and no obligation.",

  sections: [
    {
      heading: "What is the Super Visa?",
      body:
        "The Super Visa lets a parent or grandparent of a Canadian citizen or permanent resident visit their family in Canada for up to 5 years at a time. It allows multiple entries and stays valid for up to 10 years.",
      listIntro: "To qualify, the applicant must:",
      bullets: [
        "Be the parent or grandparent of a Canadian citizen or permanent resident",
        "Have a written invitation from their child or grandchild in Canada, including a promise of financial support and proof of that person's status",
        "Hold valid Super Visa medical insurance",
      ],
    },
    {
      heading: "Super Visa Insurance Requirements",
      body:
        "Super Visa insurance is the medical coverage the applicant must hold for their stay. It guarantees the family can access emergency medical care affordably if anyone is injured or falls ill during the visit.",
      listIntro: "To be accepted, the policy must:",
      bullets: [
        "Provide at least $100,000 in coverage",
        "Be valid for at least 12 months from the date of entry",
        "Cover health care, hospitalization and repatriation",
        "Be purchased before entering Canada",
        "Come with proof of payment, in full or through an approved instalment plan",
      ],
      closing:
        "Rules and approved providers change. We check every quote against the current requirements before you buy, so nothing is rejected on a technicality.",
    },
  ],

  why: [
    "Free advice consultation with an experienced Super Visa advisor, by phone or in person",
    "Quotes compared across Canada's leading insurers, not a single provider",
    "No hidden fees, ever",
    "Preexisting conditions reviewed properly before you buy",
    "In house claims assistance if you ever need to make a claim",
    "Same day partial refunds",
    "Serving Surrey and the Lower Mainland since 2000",
  ],

  ctaText:
    "Book a free consultation with an experienced Super Visa advisor. We will explain your options and find the right policy from one of our leading insurance partners.",
};

const COST_FACTORS = [
  {
    title: "Age",
    text:
      "is the single biggest factor. Premiums rise sharply after 70 and again after 80.",
  },
  {
    title: "Coverage amount",
    text:
      "is usually $100,000, though $150,000 and higher options exist and cost more.",
  },
  {
    title: "Deductible",
    text:
      "is the amount paid out of pocket before the insurer pays. Choosing a higher deductible lowers the premium, sometimes considerably.",
  },
  {
    title: "Preexisting conditions",
    text:
      "raise the premium when covered, and are worth covering in most cases.",
  },
];

const ADDITIONAL_SECTIONS = [
  {
    heading: "Monthly payment plans",
    paragraphs: [
      "You do not have to pay a full year of premium upfront. Most of our partner insurers offer monthly instalment plans that satisfy the visa requirements, with a deposit and the balance spread across the year.",
      "Monthly plans are accepted for the visa application as long as proof of the payment arrangement is provided. We will tell you which insurers offer them, what the deposit is, and whether paying annually would work out cheaper in your case.",
    ],
  },
  {
    heading: "Preexisting medical conditions",
    paragraphs: [
      "Many parents and grandparents have an existing condition such as diabetes, high blood pressure or heart disease. Coverage is usually still available, but the rules differ sharply between insurers.",
      "What matters is the stability period, meaning how long the condition has been unchanged, with no new symptoms, no new medication and no change in dosage. Some insurers require 90 days of stability, others 180 days, and some exclude certain conditions entirely.",
      "Getting this wrong is the most common reason a Super Visa claim is denied. We review the medical history with you before recommending an insurer, so the policy actually pays if it is ever needed.",
    ],
  },
  {
    heading: "Full refund if the Super Visa is refused",
    paragraphs: [
      "If the visa application is refused, you receive a 100% refund of the premium. Every policy we place carries this protection, so buying insurance before the decision carries no financial risk.",
      "Partial refunds also apply if the visit is cut short or the applicant returns home early. We process partial refunds the same day.",
    ],
  },
];

const DOCUMENT_CHECKLIST = [
  "Proof of the parent or grandparent relationship",
  "A written invitation letter from the child or grandchild in Canada",
  "Proof of that person's Canadian citizenship or permanent residence",
  "Proof that the household in Canada meets the minimum necessary income",
  "Proof of Super Visa medical insurance, with evidence of payment",
  "A completed immigration medical examination",
  "Passport and application forms",
];

const SUPER_VISA_FAQS = [
  {
    question: "How much Super Visa insurance is required?",
    answer:
      "A minimum of $100,000 in coverage for health care, hospitalization and repatriation, valid at least 12 months from the date of entry.",
  },
  {
    question: "Can I pay monthly?",
    answer:
      "Yes. Most insurers offer instalment plans accepted for the visa application. A deposit is paid upfront and the balance is spread over the year.",
  },
  {
    question: "What happens if the Super Visa is refused?",
    answer: "You receive a full refund of the premium.",
  },
  {
    question: "Can preexisting conditions be covered?",
    answer:
      "Usually yes, provided the condition has been stable for the period the insurer requires. Stability periods vary between insurers, which is why the choice of insurer matters.",
  },
  {
    question: "When should the insurance be purchased?",
    answer:
      "Before entering Canada, and before submitting the application, since proof of payment forms part of it. Buying early also leaves time to compare properly.",
  },
  {
    question: "How long can my parents stay on a Super Visa?",
    answer:
      "Up to 5 years per entry, with the visa valid for up to 10 years and multiple entries permitted.",
  },
  {
    question: "Does the policy need to be from a Canadian insurer?",
    answer:
      "The insurance must meet the current requirements for approved providers. We confirm this for every policy we place.",
  },
  {
    question: "What if they go home early?",
    answer:
      "A partial refund applies for the unused portion. We process these the same day.",
  },
];

const PAGE_COPY = {
  "critical-illness-insurance": {
    "ctaText": "Still not sure critical illness insurance is right for you? Call (604) 996 6862 for a free consultation with an experienced advisor.",
    "ctaTitle": "Ready for Critical Illness Insurance?",
    "heroLine": "Focus on recovery, not finances",
    "intro": "A serious diagnosis brings costs that public health care does not cover and often stops your income at the same time. Critical illness insurance pays a lump sum on diagnosis of a covered condition, giving you and your family room to breathe during one of the hardest periods of your life.",
    "meta": {
      "description": "Critical illness insurance compared across Canada's top insurers. Lump sum benefit on diagnosis. Free consultation with a licensed advisor. Call (604) 996 6862.",
      "title": "Critical Illness Insurance Canada | Lump Sum Cover | (604) 996 6862"
    },
    "sections": [
      {
        "bullets": [
          "No worrying about medical bills or mounting debt",
          "Access to treatments and medications not covered by provincial health plans",
          "Replacement of lost income if you or your spouse cannot work",
          "Help around the house while you recover",
          "Travel and accommodation if treatment is far from home",
          "The freedom to focus on recovery instead of finances"
        ],
        "heading": "Why you need critical illness coverage",
        "imageIndex": 0,
        "paragraphs": [
          "The money is yours to use however you need. There are no restrictions on how the benefit is spent."
        ]
      },
      {
        "closing": [
          "We go through the exclusions with you before you buy, not after a claim is refused."
        ],
        "entries": [
          {
            "text": "Most policies pay only if you survive a set number of days after diagnosis, commonly 30. Check it.",
            "title": "The survival period."
          },
          {
            "text": "Early stage cancers are frequently excluded or paid at a reduced rate. Two policies that both say they cover cancer can behave very differently in practice.",
            "title": "The severity definitions."
          }
        ],
        "heading": "What is covered",
        "imageIndex": 1,
        "paragraphs": [
          "Covered conditions differ from insurer to insurer. There is no universal list. Most plans cover cancer, heart attack and stroke, and comprehensive plans extend to 25 or more conditions including multiple sclerosis, Parkinson's disease, major organ transplant and loss of independence.",
          "Two details decide whether a policy is worth holding."
        ]
      },
      {
        "heading": "How much coverage do you need?",
        "paragraphs": [
          "A common approach is to cover one to two years of household income, which buys time to stop working, get treatment and recover without touching savings or retirement funds.",
          "If your goal is narrower, such as covering a mortgage payment for two years or funding treatment not available through the provincial plan, a smaller amount will do. We will work out a figure that reflects what you would actually need rather than an arbitrary round number."
        ]
      },
      {
        "heading": "Return of premium options",
        "paragraphs": [
          "Many critical illness policies offer a return of premium rider. If you never claim, you get your premiums back, either at a set date or when the policy ends.",
          "It raises the cost meaningfully. For people who dislike the idea of paying for something they may never use, it can make the decision easier. For people focused purely on protection per dollar, the plain policy is better value. Both are defensible and we will show you the numbers for each."
        ]
      }
    ],
    "title": "Critical Illness Insurance",
    "why": [
      "Free consultation with an experienced critical illness advisor",
      "Coverage compared across leading Canadian insurers",
      "Exclusions explained clearly before you commit",
      "No hidden fees",
      "In house claims assistance when it matters most",
      "Reliable coverage from Canada's best insurers since 2000"
    ]
  },
  "disability-insurance": {
    "ctaText": "Call (604) 996 6862 and we will find the right plan for your financial needs now and in the future.",
    "ctaTitle": "Ready for Disability Insurance?",
    "heroLine": "Protect your most valuable asset",
    "intro": "Your ability to earn is almost certainly your largest financial asset. Disability insurance pays a monthly benefit if illness or injury stops you working, covering your expenses while you recover.",
    "meta": {
      "description": "Short term and long term disability insurance compared across Canadian insurers. Protect your income if illness or injury stops you working. Call (604) 996 6862.",
      "title": "Disability Insurance Canada | Income Protection Cover | (604) 996 6862"
    },
    "sections": [
      {
        "bullets": [
          "A monthly benefit replacing part of your income while you cannot work",
          "Everyday living costs, bills and commitments covered",
          "Short term and long term options",
          "Coverage that follows you between jobs, unlike most group plans"
        ],
        "heading": "Why disability insurance matters",
        "imageIndex": 0,
        "paragraphs": [
          "Most people insure their car and their home without hesitating, then leave the income that pays for both completely unprotected. A disability lasting months or years interrupts everything, and savings rarely stretch as far as people expect."
        ]
      },
      {
        "closing": [
          "If you have to choose one, choose long term. A short absence is survivable. A permanent one is not."
        ],
        "entries": [
          {
            "text": "typically begins within days or weeks of the disability and pays for a few months. It bridges the gap before long term coverage starts.",
            "title": "Short term disability"
          },
          {
            "text": "begins after a waiting period, usually 90 or 120 days, and can pay through to age 65. This is the coverage that protects against genuine financial ruin.",
            "title": "Long term disability"
          }
        ],
        "heading": "Short term and long term coverage"
      },
      {
        "closing": [
          "A policy that looks cheaper is frequently just a weaker definition. We will show you exactly which one you are being quoted."
        ],
        "entries": [
          {
            "text": "pays if you cannot perform the duties of your own job, even if you could work in another. It is the strongest wording and it costs more.",
            "title": "Own occupation"
          },
          {
            "text": "pays only if you cannot work in any job you are reasonably suited to by education, training or experience. It is cheaper and much harder to claim on.",
            "title": "Any occupation"
          },
          {
            "text": "sits between the two, often own occupation for a set period and any occupation after.",
            "title": "Regular occupation"
          }
        ],
        "heading": "Why the definition of disability decides everything",
        "paragraphs": [
          "This is the most important clause in a disability policy and the one almost nobody reads."
        ]
      },
      {
        "heading": "If you are self employed",
        "paragraphs": [
          "Employees often have some group coverage through work. Self employed people have none, and Employment Insurance sickness benefits are limited and short.",
          "If you are self employed or a contractor, individual disability coverage is not a nice to have. It is the only thing standing between an injury and no income at all. Underwriting is more involved because income has to be established, so start the conversation earlier than you think you need to."
        ]
      },
      {
        "bullets": [
          "Assessment of your income and your actual obligations",
          "Comparison of waiting periods, benefit periods and definitions",
          "Clear advice with no pressure"
        ],
        "heading": "Finding the right plan",
        "imageIndex": 1,
        "paragraphs": [
          "When you speak with an advisor, share as much as you can about your income, your regular expenses and what you can afford in premium. From there we compare waiting periods, benefit periods and definitions of disability across insurers and recommend a plan that fits both now and later."
        ]
      }
    ],
    "title": "Disability Insurance",
    "why": [
      "Free consultation with an experienced disability insurance advisor",
      "No hidden fees",
      "Recommendations built around your income and lifestyle",
      "In house claims assistance",
      "Trusted by Canadian families since 2000"
    ]
  },
  "life-insurance": {
    "ctaText": "Book a free consultation with an experienced life insurance advisor. Call (604) 996 6862.",
    "ctaTitle": "Ready for Life Insurance?",
    "heroLine": "The gift of financial security",
    "intro": "Life insurance matters for anyone who provides for a family. Its purpose is simple: to protect the people who depend on you from financial loss if you die, so they stay in their home, pay their bills and keep up with everyday life.",
    "meta": {
      "description": "Compare life insurance across Canada's top insurers. Term, whole and permanent policies explained plainly by a licensed advisor in Surrey BC. Call (604) 996 6862.",
      "title": "Life Insurance Canada | Term, Whole and Permanent Cover | (604) 996 6862"
    },
    "sections": [
      {
  "closing": [
    "Most people are best served by term. Some are genuinely better off with permanent coverage. The honest answer depends on why you want the policy and how long you need it to last, and we will tell you plainly which one fits."
  ],
  "entries": [
    {
      "text": "Covers you for a fixed period, usually 10, 20 or 30 years. It is by far the cheapest way to buy a large amount of coverage, which makes it the right answer for most families with a mortgage and children at home. If you outlive the term, it pays nothing. Many term policies can be converted to permanent coverage later without a new medical.",
      "title": "Term life insurance"
    },
    {
      "text": "Covers you for life and builds cash value you can borrow against. Premiums are considerably higher than term for the same death benefit, but they never rise and the policy never expires. It suits estate planning, leaving a guaranteed inheritance, and covering final expenses.",
      "title": "Whole life insurance"
    },
    {
      "text": "Lifelong coverage with an investment component and flexible premiums. It offers more control than whole life and more complexity with it. It generally makes sense for higher income earners who have already maximised their RRSP and TFSA room.",
      "title": "Universal and permanent life insurance"
    }
  ],
  "heading": "The three types of life insurance in Canada",
  "imageIndex": 0,
  "hideImage": true
},
      {
        "closing": [
          "Then subtract what you already have, including any coverage through work. Employer coverage is worth counting, but remember it usually ends when the job does.",
          "The total is a reasonable estimate of what your family would need to carry on without your income. We will run the numbers with you in a single conversation."
        ],
        "entries": [
          {
            "text": "Loans, credit cards and car payments, excluding the mortgage.",
            "title": "Debts."
          },
          {
            "text": "Your annual income multiplied by the number of years your family would need support. Ten years is a common starting point.",
            "title": "Income."
          },
          {
            "text": "Enough to clear the mortgage completely.",
            "title": "Mortgage."
          },
          {
            "text": "Estimated future education costs for each child.",
            "title": "Education."
          }
        ],
        "heading": "How much life insurance do you need?",
        "paragraphs": [
          "A straightforward way to arrive at a figure is to add up four things."
        ]
      },
      {
        "heading": "What life insurance costs",
        "paragraphs": [
          "Premiums are driven by age, health, smoking status, the coverage amount and the length of the term. Age matters most, and it only moves in one direction. The same policy bought at 35 costs materially less than at 45, and that difference is locked in for the life of the term.",
          "Smokers pay roughly two to three times what nonsmokers pay. Most insurers will reclassify you at nonsmoker rates after a set period without tobacco, which is worth asking about.",
          "No medical policies exist for people who cannot qualify for standard underwriting. They cost more for the same coverage, so they are a fallback rather than a first choice."
        ]
      },
      {
        "bullets": [
          "A full needs analysis, not a guess",
          "Side by side comparison across insurers, with the differences explained",
          "No pressure and no artificial deadlines",
          "Support 7 days a week, including after the policy is in force"
        ],
        "heading": "What to expect from your advisor",
        "imageIndex": 1,
        "paragraphs": [
          "Your advisor will ask about your age, your family, your income and your obligations. From there we compare policies across Canada's leading insurers, explain each option in plain language, and place the policy only once you are genuinely confident in the decision."
        ]
      },
      {
        "heading": "What happens if you have been declined before",
        "paragraphs": [
          "A declined application is not the end of it. Insurers underwrite differently, and a decline from one does not mean a decline from all. Ask for the specific reason, check the underlying information is accurate, then apply to an insurer whose rules suit your circumstances. An advisor who knows those differences is the difference between a second decline and a policy in force."
        ]
      }
    ],
    "title": "Life Insurance",
    "why": [
      "Fully licensed life insurance agency",
      "Free consultations by phone or in person, with no obligation",
      "Full Financial Needs Analysis before any recommendation",
      "No hidden fees",
      "In house claims assistance for beneficiaries",
      "Helping Canadians since 2000, available 7 days a week"
    ]
  },
  "visitor-visa-insurance": {
    "ctaText": "Do not wait until it is too late. It is best to arrange visitor insurance before arriving in Canada. Call (604) 996 6862 to speak with an advisor today.",
    "ctaTitle": "Ready for Visitor Insurance?",
    "heroLine": "Prepare for the unexpected while you are travelling",
    "intro": "Visitor insurance matters for anyone planning to come to Canada, whether for a short visit, an extended stay, or as a new permanent resident waiting for provincial health coverage to begin. While you are here, it protects you against the cost of an accident or a medical emergency.",
    "meta": {
      "description": "Emergency medical insurance for visitors to Canada. Coverage for short trips, extended stays and new arrivals waiting on provincial health cover. Call (604) 996 6862.",
      "title": "Visitor Insurance Canada | Travel Medical Cover for Visitors | (604) 996 6862"
    },
    "sections": [
      {
        "bullets": [
          "Affordable protection against emergency medical bills",
          "Some policies also cover you while travelling to and from Canada",
          "Side trips to other countries can often be included"
        ],
        "heading": "Why you need visitor insurance in Canada",
        "imageIndex": 0,
        "paragraphs": [
          "Canada's public health coverage applies only to citizens and lawful residents. If you are visiting, or waiting out the provincial waiting period after arriving, you are responsible for your own medical costs. Without insurance, a single emergency can cost thousands of dollars."
        ]
      },
      {
        "heading": "What visitor insurance costs",
        "paragraphs": [
          "Premiums depend on age, the length of stay, the coverage amount and the deductible. A healthy visitor in their forties staying three months pays a fraction of what a visitor in their seventies pays for the same policy.",
          "Rates vary widely between insurers for identical coverage, so comparing is worth the time. Raising the deductible is the simplest way to bring a premium down without cutting the coverage amount.",
          "We will quote across our partner insurers and show you what each one actually covers, not just the price."
        ]
      },
      {
        "heading": "Visitors with preexisting conditions",
        "paragraphs": [
          "Coverage is often available for visitors with existing medical conditions, provided the condition has been stable for the period the insurer requires. Stability rules and excluded conditions differ from one insurer to the next.",
          "This is the detail that decides whether a claim is paid. We review the medical history first and match you to an insurer whose rules actually fit the situation."
        ]
      },
      {
        "heading": "New permanent residents and the provincial waiting period",
        "paragraphs": [
          "New arrivals to British Columbia are not covered by MSP immediately. There is a waiting period before provincial coverage begins, and during that time you are responsible for your own medical costs.",
          "Short term visitor policies cover exactly this gap. If you have recently landed and are waiting on MSP, speak with us before something goes wrong rather than after."
        ]
      },
      {
        "bullets": [
          "Valid for the full length of the stay",
          "Coverage from an insurer that knows the Canadian health system",
          "Honest guidance on the right coverage amount for your plans and budget"
        ],
        "heading": "What coverage do you need?",
        "imageIndex": 1,
        "paragraphs": [
          "We recommend a policy covering accidents and medical emergencies for the entire time you are in Canada, including travel to and from the country."
        ]
      }
    ],
    "title": "Visitor Visa Insurance",
    "why": [
      "Free consultation with an experienced visitor insurance advisor",
      "Quotes compared across Canada's leading insurers",
      "No hidden fees",
      "Preexisting conditions reviewed before you buy",
      "Same day partial refunds if plans change",
      "Serving visitors and newcomers since 2000"
    ]
  }
};

function useServiceMetadata(meta) {
  useEffect(() => {
    if (!meta) return;

    const previousTitle = document.title;
    const existingDescription = document.head.querySelector(
      'meta[name="description"]'
    );
    const previousDescription =
      existingDescription?.getAttribute("content") ?? null;

    const description =
      existingDescription || document.createElement("meta");

    if (!existingDescription) {
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    document.title = meta.title;
    description.setAttribute("content", meta.description);

    return () => {
      document.title = previousTitle;

      if (!existingDescription) {
        description.remove();
      } else if (previousDescription === null) {
        description.removeAttribute("content");
      } else {
        description.setAttribute("content", previousDescription);
      }
    };
  }, [meta]);
}

/*
 * Same structure, image and styling as the existing PageHero.
 * The two buttons are added only for the Super Visa page.
 */
function SuperVisaHero({ service }) {
  const { openQuote } = useQuote();

  return (
    <section className="page-hero relative isolate min-h-[430px] overflow-hidden bg-[#073B67] lg:min-h-[510px]">
      <div className="page-hero-media absolute inset-0">
        <img
          src={service.heroImage || service.image}
          alt={`${service.title} support and coverage`}
          className="h-full w-full object-cover object-center"
        />
      </div>

      <div className="pointer-events-none absolute inset-0 bg-[#073B67]/70" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#052F54]/80 via-[#073B67]/35 to-[#073B67]/10" />
      <div className="pointer-events-none absolute -left-28 -top-28 h-80 w-80 rounded-full bg-[#0785C7]/20 blur-3xl" />

      <div className="relative mx-auto flex min-h-[430px] max-w-[1440px] items-center lg:min-h-[510px]">
        <div className="relative z-10 px-6 py-16 sm:py-20 lg:px-12 lg:py-24 xl:pl-20">
          <div className="hero-stagger max-w-2xl">
            <PillBadge light>{service.heroLine}</PillBadge>

            <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[56px]">
              {service.title}
            </h1>

            <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[#D7EAF5] sm:text-[17px]">
              {service.intro}
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={() => openQuote(service.title)}
                className="btn-light group"
                data-testid="super-visa-hero-quote"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
              </button>

           <a
  href={PHONE_HREF}
  className="btn-outline-light group"
  data-testid="super-visa-hero-call"
>
  Talk to an Advisor

  <ArrowUpRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SuperVisaAdditionalContent() {
  return (
    <>
      {/* Cost */}
      <section data-reveal aria-labelledby="super-visa-cost-heading">
        <h2
          id="super-visa-cost-heading"
          className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl"
        >
          How much does Super Visa insurance cost?
        </h2>

        <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
          Cost depends on four things: the applicant's age, the coverage
          amount, the deductible chosen, and whether preexisting conditions
          need covering.
        </p>

        <div className="mt-6 overflow-hidden rounded-2xl border border-[#E1ECF3] bg-[#F4F7FB]">
          {COST_FACTORS.map((factor, index) => (
            <div
              key={factor.title}
              className={[
                "px-6 py-5",
                index > 0 ? "border-t border-[#E1ECF3]" : "",
              ].join(" ")}
            >
              <p className="text-[15px] leading-relaxed text-[#526A7D]">
                <strong className="font-semibold text-[#0B2748]">
                  {factor.title}
                </strong>{" "}
                {factor.text}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[15.5px] leading-relaxed text-[#526A7D]">
          Rates differ substantially between insurers for identical
          coverage. That gap is where an advisor earns their keep. We
          compare across our partner insurers and show you the real
          difference in writing, not a single quote you have no way to
          judge.
        </p>

        <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
          <a
            href={PHONE_HREF}
            className="font-semibold text-[#006FB9] hover:underline"
          >
            Call {PHONE}
          </a>{" "}
          for a quote based on the actual ages and health of the people
          travelling.
        </p>
      </section>

      {/* Monthly payments, medical conditions and refunds */}
      {ADDITIONAL_SECTIONS.map((section) => (
        <section
          key={section.heading}
          data-reveal
          className="border-t border-[#E1ECF3] pt-8"
        >
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl">
            {section.heading}
          </h2>

          <div className="mt-4 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>
      ))}

      {/* Document checklist */}
      <section
        data-reveal
        className="rounded-3xl border border-[#E1ECF3] bg-[#F4F7FB] p-6 sm:p-8"
        aria-labelledby="super-visa-checklist-heading"
      >
        <h2
          id="super-visa-checklist-heading"
          className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl"
        >
          Super Visa document checklist
        </h2>

        <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
          Alongside the insurance policy, the application generally requires:
        </p>

        <ul className="aligned-icon-list mt-6 space-y-4">
          {DOCUMENT_CHECKLIST.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-[15px] leading-relaxed text-[#193B58]"
            >
              <CheckCircle2
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p className="mt-6 border-t border-[#DCE8F0] pt-5 text-[15px] leading-relaxed text-[#526A7D]">
          Requirements change from time to time. Check the current list on
          the Government of Canada website before submitting, and we will
          confirm the insurance portion is correct.
        </p>
      </section>
    </>
  );
}

function SuperVisaFAQ() {
  return (
    <section data-reveal aria-labelledby="super-visa-faq-heading">
      <h2
        id="super-visa-faq-heading"
        className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl"
      >
        Super Visa Insurance FAQ
      </h2>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E1ECF3] bg-white">
        {SUPER_VISA_FAQS.map((faq, index) => (
          <details
            key={faq.question}
            className={[
              "group",
              index > 0 ? "border-t border-[#E1ECF3]" : "",
            ].join(" ")}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-5 text-left font-heading text-[16px] font-bold leading-relaxed text-[#0B2748] transition-colors hover:bg-[#F4F7FB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#006FB9] sm:px-6 [&::-webkit-details-marker]:hidden">
              <span>{faq.question}</span>
              <ChevronDown
                aria-hidden="true"
                className="h-5 w-5 shrink-0 text-[#006FB9] transition-transform duration-200 group-open:rotate-180"
              />
            </summary>

            <div className="px-5 pb-5 sm:px-6">
              <p className="text-[15px] leading-relaxed text-[#526A7D]">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function ServiceLocation({ slug, showHours = false }) {
  const headingId = `${slug}-location-heading`;

  return (
    <section
      data-reveal
      aria-labelledby={headingId}
      className="overflow-hidden rounded-3xl border border-[#E1ECF3] bg-[#F4F7FB]"
    >
      {/* Location heading */}
      <div className="border-b border-[#E1ECF3] px-6 py-6 text-center lg:px-8 lg:text-left">
        <h2
          id={headingId}
          className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] lg:text-3xl"
        >
          Our <span className="text-[#0785C7]">Location</span>
        </h2>
      </div>

      <div className="px-6 py-7 lg:px-8">
        {/* Agency name */}
        <h3 className="text-center font-heading text-lg font-bold text-[#0B2748] lg:text-left lg:text-xl">
          Abhi Insurance Agency Inc.
        </h3>

        <address className="mt-6 grid grid-cols-1 gap-5 text-[15px] not-italic leading-relaxed text-[#526A7D] lg:grid-cols-2 lg:gap-6">
         
         {/* Address */}
<a
  href={MAP_URL}
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Open Abhi Insurance Agency location in Google Maps"
  className="group mx-auto grid w-full max-w-[250px] grid-cols-[22px_minmax(0,1fr)] items-start gap-3 transition-colors hover:text-[#006FB9] lg:mx-0 lg:max-w-none"
>
  <MapPin
    aria-hidden="true"
    className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]"
  />

  <span className="text-left group-hover:underline">
    8128 128 St #302
    <span className="block">
      Surrey, BC V3W 1R1
    </span>
  </span>

 
</a>

          {/* Phone and email */}
          <div className="mx-auto grid w-full max-w-[250px] grid-cols-1 gap-5 lg:mx-0 lg:max-w-none lg:gap-4">
            <p className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-3">
              <Phone
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]"
              />

              <span className="min-w-0 text-left">
                Phone:{" "}
                <a
                  href={PHONE_HREF}
                  className="font-medium text-[#006FB9] hover:underline"
                >
                  {PHONE}
                </a>
              </span>
            </p>

            <p className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-3">
              <Mail
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]"
              />

              <span className="min-w-0 text-left">
                Email:{" "}
                <a
                  href={`mailto:${EMAIL}`}
                  className="break-words font-medium text-[#006FB9] hover:underline"
                >
                  {EMAIL}
                </a>
              </span>
            </p>
          </div>
        </address>

        {/* Opening hours */}
        {showHours && (
          <p className="mt-6 border-t border-[#DCE8F0] pt-5 text-center text-[15px] text-[#526A7D] lg:text-left">
            Open 7 days a week, 9:00 to 17:00
          </p>
        )}
      </div>
    </section>
  );
}


function PhoneText({ text }) {
  return text.split(PHONE).map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && (
        <a href={PHONE_HREF} className="hover:underline">
          {PHONE}
        </a>
      )}
      {part}
    </React.Fragment>
  ));
}

/*
 * Only the two existing section images are rendered on each page.
 * imageIndex preserves their original order/crop while text sections
 * follow the order in the supplied document.
 */
function ServiceContentSection({ section, service, index }) {
  const hasImage =
    Number.isInteger(section.imageIndex) && !section.hideImage;

  const imageOnLeft = hasImage && section.imageIndex % 2 === 1;
  const headingId = `${service.slug}-section-${index}`;

  return (
    <section
      data-reveal
      aria-labelledby={headingId}
      className={
        section.hideImage
          ? "w-full"
          : hasImage
            ? `grid grid-cols-1 items-center gap-10 ${
                imageOnLeft
                  ? "lg:grid-cols-[1fr_1.2fr]"
                  : "lg:grid-cols-[1.2fr_1fr]"
              }`
            : "border-t border-[#E1ECF3] pt-8"
      }
    >
      <div
        className={`${
          imageOnLeft ? "min-w-0 lg:order-2" : "min-w-0"
        } ${section.hideImage ? "w-full" : ""}`}
      >
        <h2
          id={headingId}
          className={`font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl ${
            section.hideImage ? "max-w-3xl" : ""
          }`}
        >
          {section.heading}
        </h2>

        {section.paragraphs && (
          <div className="mt-4 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        )}

        {section.entries && (
          <div
            className={`mt-5 space-y-5 text-[15.5px] leading-relaxed text-[#526A7D] ${
              section.hideImage ? "w-full" : ""
            }`}
          >
            {section.entries.map((entry) => (
              <p key={entry.title}>
                <strong className="font-bold text-[#193B58]">
                  {entry.title}
                </strong>{" "}
                {entry.text}
              </p>
            ))}
          </div>
        )}

        {section.bullets && (
          <ul className="aligned-icon-list mt-6 space-y-3">
            {section.bullets.map((bullet) => (
              <li
                key={bullet}
                className="flex items-start gap-3 text-[15px] leading-relaxed text-[#526A7D]"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}

        {section.closing && (
          <div className="mt-5 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
            {section.closing.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        )}
      </div>

      {hasImage && (
        <div
          className={`${
            imageOnLeft ? "lg:order-1" : ""
          } overflow-hidden rounded-3xl`}
        >
          <img
            src={
              service.sectionImages?.[section.imageIndex] ||
              service.image
            }
            alt={section.heading}
            className="h-72 w-full object-cover object-center transition-transform duration-700 hover:scale-105"
          />
        </div>
      )}
    </section>
  );
}

function ServiceDetailContent({ originalService }) {
  const isSuperVisa = originalService.slug === SUPER_VISA_SLUG;

  // Page-specific copy never overwrites shared mock.js data or image URLs.
  const pageCopy = isSuperVisa
    ? SUPER_VISA_COPY
    : PAGE_COPY[originalService.slug];
  const service = pageCopy
    ? { ...originalService, ...pageCopy }
    : originalService;
  const meta = isSuperVisa
    ? SUPER_VISA_META
    : PAGE_COPY[originalService.slug]?.meta;

  const ref = useRevealGroup();
  const { openQuote } = useQuote();

  useServiceMetadata(meta);

  const Icon = SERVICE_ICONS[service.icon] || ShieldCheck;
  const others = SERVICES.filter((item) => item.slug !== service.slug);

  return (
    <div ref={ref}>
    <SuperVisaHero service={service} />

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-[1fr_360px]">
          {/* Main content */}
          <div className="min-w-0 space-y-12">
            {isSuperVisa ? service.sections.map((section, index) => (
              <div
                key={section.heading}
                data-reveal
                className={`grid grid-cols-1 items-center gap-10 ${
                  index % 2 === 0
                    ? "lg:grid-cols-[1.2fr_1fr]"
                    : "lg:grid-cols-[1fr_1.2fr]"
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl">
                    {section.heading}
                  </h2>

                  <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
                    {section.body}
                  </p>

                  {section.listIntro && (
                    <p className="mt-5 text-[15.5px] font-semibold leading-relaxed text-[#193B58]">
                      {section.listIntro}
                    </p>
                  )}

                  <ul className="aligned-icon-list mt-6 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-3 text-[15px] text-[#193B58]"
                      >
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" />
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {section.closing && (
                    <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">
                      {section.closing}
                    </p>
                  )}
                </div>

                <div
                  className={`${
                    index % 2 === 1 ? "lg:order-1" : ""
                  } overflow-hidden rounded-3xl`}
                >
                  <img
                    src={service.sectionImages?.[index] || service.image}
                    alt={section.heading}
                    className="h-72 w-full object-cover object-center transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </div>
            )) : service.sections.map((section, index) => (
              <ServiceContentSection
                key={section.heading}
                section={section}
                service={service}
                index={index}
              />
            ))}

            {isSuperVisa && <SuperVisaAdditionalContent />}

            {/* Why choose us */}
            <div
              data-reveal
              className={
                isSuperVisa
                  ? "rounded-3xl bg-[#006FB9] p-6 text-white sm:p-10"
                  : "rounded-3xl bg-[#006FB9] p-10 text-white sm:p-12"
              }
            >
              <PillBadge light>
                Your satisfaction is 100% guaranteed
              </PillBadge>

              <h2 className="mt-4 font-heading text-2xl font-extrabold sm:text-3xl">
                Why choose Abhi Insurance Agency
              </h2>

              <ul className="aligned-icon-list mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {service.why.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[15px] leading-relaxed text-[#D9ECF7]"
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#B9DFF3]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {isSuperVisa && <SuperVisaFAQ />}
            <ServiceLocation slug={service.slug} showHours={isSuperVisa} />

            {/* Existing consultation strip */}
            <div
              data-reveal
              className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-[#E1ECF3] bg-[#F4F7FB] p-8 lg:flex-row lg:items-center"
            >
              <p className="max-w-xl text-[15.5px] font-medium leading-relaxed text-[#193B58]">
                <PhoneText text={service.ctaText} />
              </p>

              <ScheduleCallButton
                className="btn-primary shrink-0"
                testId="service-call-btn"
              />
            </div>
          </div>

          {/* Existing sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div
              data-reveal
              className="rounded-2xl bg-white p-7 shadow-[0_4px_24px_rgba(0,111,185,0.09)]"
            >
              <span className="service-quote-icon grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA]">
                <Icon className="h-6 w-6 text-[#006FB9]" />
              </span>

              <h3 className="mt-4 font-heading text-lg font-bold text-[#0B2748]">
                Get a {service.title} quote
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-[#526A7D]">
                Free consultation, best available rates, no hidden fees.
              </p>

              <button
                type="button"
                onClick={() => openQuote(service.title)}
                className="btn-primary group mt-5 w-full justify-center"
                data-testid="sidebar-quote-btn"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            <div
              data-reveal
              className="rounded-2xl border border-[#E1ECF3] bg-white p-7"
            >
              <h4 className="font-heading text-base font-bold text-[#0B2748]">
                Other Services
              </h4>

              <ul className="mt-4 space-y-1">
                {others.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/services/${other.slug}`}
                      className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-[#294A65] transition-colors duration-200 hover:bg-[#EEF6FB] hover:text-[#006FB9]"
                    >
                      {other.title}
                      <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-reveal
              className="rounded-2xl bg-[#E2F2FA] p-7"
            >
              <p className="font-heading text-base font-bold text-[#0B2748]">
                Talk to a human, 7 days a week
              </p>

              <ScheduleCallButton
                className="btn-outline mt-4 w-full justify-center"
                testId="sidebar-schedule-call"
              />

              <p className="mt-1.5 text-sm text-[#45647C]">
                {isSuperVisa
                  ? "Open 7 days a week, 9:00 to 17:00"
                  : COMPANY.hours}
              </p>
            </div>
          </aside>
        </div>
      </section>

      <CTASection
        title={service.ctaTitle || `Ready for ${service.title}?`}
        subtitle={
          isSuperVisa ? (
            <>
              {service.ctaText}
              <span className="mt-4 block">
                <a href={PHONE_HREF} className="hover:underline">
                  Call {PHONE}
                </a>
                {" · "}
                <a
                  href={`mailto:${EMAIL}`}
                  className="break-words hover:underline"
                >
                  {EMAIL}
                </a>
                {" · Available 7 days a week"}
              </span>
            </>
          ) : (
            <PhoneText text={service.ctaText} />
          )
        }
      />

     <section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {PAGE_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  /*
   * Remount the content when switching service routes so reveal
   * observers and page metadata initialize for the selected page.
   */
  return (
    <ServiceDetailContent
      key={service.slug}
      originalService={service}
    />
  );
}
